import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { isParentKycRequiredError } from '../../../identity/helpers/identity-error.helper';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { FamilyHubService } from '../../services/family-hub.service';

export type FamilyInviteKind = 'co-parent' | 'caregiver';

export type FamilyInviteDialogData = {
  kind?: FamilyInviteKind;
  softPromptParentalFigure?: boolean;
};

@Component({
  selector: 'app-co-parent-invite-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslatePipe,
    ParentButtonComponent,
  ],
  templateUrl: './co-parent-invite-dialog.component.html',
  styleUrl: './co-parent-invite-dialog.component.scss',
})
export class CoParentInviteDialogComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<CoParentInviteDialogComponent>);
  #familyHub = inject(FamilyHubService);
  #translate = inject(TranslateService);
  #identityNav = inject(IdentityNavigationService);

  data = inject<FamilyInviteDialogData>(MAT_DIALOG_DATA, {
    optional: true,
  });

  readonly kind = computed<FamilyInviteKind>(
    () => this.data?.kind ?? 'co-parent',
  );

  readonly i18nPrefix = computed(() =>
    this.kind() === 'caregiver'
      ? 'app.familyHub.inviteExtended'
      : 'app.familyHub.invite',
  );

  invite = signal<AuthGen.CoParentInviteResult | null>(null);
  infoMessage = signal('');
  errorMessage = signal('');
  isGenerating = computed(
    () =>
      this.kind() === 'caregiver'
        ? this.#familyHub.inviteCaregiver.isPending()
        : this.#familyHub.inviteCoParent.isPending(),
  );

  ngOnInit(): void {
    void this.generate();
  }

  discard(): void {
    this.#dialogRef.close();
  }

  async generate(): Promise<void> {
    this.errorMessage.set('');
    try {
      const result =
        this.kind() === 'caregiver'
          ? await this.#familyHub.inviteCaregiver.mutateAsync()
          : await this.#familyHub.inviteCoParent.mutateAsync();
      this.invite.set(result);
    } catch (error) {
      if (isParentKycRequiredError(error)) {
        this.#dialogRef.close();
        await this.#identityNav.navigateToVerification();
        return;
      }
      this.errorMessage.set(
        this.#translate.instant(`${this.i18nPrefix()}.generateError`),
      );
    }
  }

  async copyLink(): Promise<void> {
    const url = this.invite()?.webUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.infoMessage.set(
        this.#translate.instant(`${this.i18nPrefix()}.linkCopied`),
      );
    } catch {
      this.infoMessage.set(url);
    }
  }

  async shareInvite(): Promise<void> {
    const webUrl = this.invite()?.webUrl;
    const qrImage = this.invite()?.qrImageBase64;
    if (!webUrl) return;

    const title = this.#translate.instant(`${this.i18nPrefix()}.shareTitle`);
    const text = this.#translate.instant(`${this.i18nPrefix()}.shareText`);
    const shareData: ShareData = { title, text, url: webUrl };

    if (!navigator.share) {
      await this.copyLink();
      return;
    }

    try {
      if (qrImage) {
        const qrFile = await this.dataUrlToFile(
          qrImage,
          this.kind() === 'caregiver'
            ? 'valvur-caregiver-invite-qr.png'
            : 'valvur-co-parent-invite-qr.png',
        );
        if (qrFile) {
          const withFile: ShareData = { ...shareData, files: [qrFile] };
          if (!navigator.canShare || navigator.canShare(withFile)) {
            await navigator.share(withFile);
            this.infoMessage.set(
              this.#translate.instant(`${this.i18nPrefix()}.shared`),
            );
            return;
          }
        }
      }
      await navigator.share(shareData);
      this.infoMessage.set(
        this.#translate.instant(`${this.i18nPrefix()}.shared`),
      );
    } catch {
      await this.copyLink();
    }
  }

  private async dataUrlToFile(
    dataUrl: string,
    filename: string,
  ): Promise<File | null> {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      return new File([blob], filename, { type: blob.type || 'image/png' });
    } catch {
      return null;
    }
  }
}
